import { useRef, useEffect } from "react";
import { ChatList } from "../components/ChatList";
import { ChatInput } from "../components/ChatInput";
import { Header } from "../components/Header";
import { useChat } from "../utils/chatContext";
import logoUrl from "../assets/ctsi-buffalo-logo.png";

const SUGGESTIONS = [
  {
    label: "Find collaborators",
    prompt:
      "I'm researching diabetes and would like to find collaborators at UB",
  },
  {
    label: "Explore CTSI services",
    prompt: "What biostatistics and study-design support does CTSI provide?",
  },
  {
    label: "Find an expert",
    prompt: "Who at UB specializes in cancer immunotherapy?",
  },
  {
    label: "Contact the right team",
    prompt: "How do I request a consultation with the CTSI informatics core?",
  },
];

export function ChatPage({
  isAdmin = false,
  onOpenReview,
}: {
  isAdmin?: boolean;
  onOpenReview?: () => void;
}) {
  const { messages, setInput, input, sendMessage, isLoading, clearChat } =
    useChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isFresh = messages.length <= 1 && !isLoading;

  useEffect(() => {
    // Keep the welcome screen (logo first) at the top; follow the
    // conversation once it has started.
    if (scrollRef.current && !isFresh) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isFresh]);

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa]">
      {isAdmin ? (
        <Header onOpenReview={onOpenReview} />
      ) : (
        // Embedded chat: no title bar, just the new-conversation control.
        <div className="flex justify-end px-2 pt-2 shrink-0">
          <button
            onClick={clearChat}
            disabled={isLoading}
            className="p-2 text-slate-400 hover:text-[#005bbb] hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
            title="New conversation"
          >
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.7}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
              />
            </svg>
          </button>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll">
        {isFresh ? (
          <div className="max-w-3xl mx-auto px-4 pt-[8vh] pb-6">
            <div className="text-center mb-8 message-enter">
              <img
                src={logoUrl}
                alt="CTSI Buffalo"
                className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-lg shadow-blue-900/20"
              />
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
                How can we help your research?
              </h1>
              <p className="text-[15px] text-slate-500 mt-2">
                CTSI services, UB faculty expertise, staff contacts, and
                clinical trials — answered with live sources.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-2.5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => sendMessage(s.prompt)}
                  disabled={isLoading}
                  style={{ animationDelay: `${i * 60}ms` }}
                  className="message-enter text-left group rounded-2xl border border-slate-200/90 bg-white hover:border-[#005bbb]/40 hover:shadow-md hover:shadow-blue-900/5 transition-all px-4 py-3.5 disabled:opacity-50"
                >
                  <div
                    className={`flex items-center gap-2 text-[13px] font-medium text-[#005bbb] ${isAdmin ? "mb-1" : ""}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#005bbb]/60 group-hover:bg-[#005bbb] transition-colors" />
                    {s.label}
                  </div>
                  {isAdmin && (
                    <div className="text-[13.5px] text-slate-500 leading-snug">
                      {s.prompt}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {messages.length === 1 && <ChatList messages={messages} />}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-6">
            <ChatList messages={messages} />
          </div>
        )}
      </div>

      <div className="border-t border-slate-200/70 bg-[#f7f8fa]/85 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 pt-3 pb-4">
          <ChatInput
            value={input}
            onChange={setInput}
            onSubmit={sendMessage}
            disabled={isLoading}
            placeholder="Ask about CTSI services, UB researchers, funding, trials…"
          />
          <p className="text-center text-[11px] text-slate-400 mt-2.5">
            Answers are grounded in live UB sources · verify important details ·{" "}
            <a
              href="https://www.research.buffalo.edu/portal/ctsa/index"
              target="_blank"
              rel="noreferrer"
              className="text-[#005bbb]/80 hover:text-[#005bbb]"
            >
              CTSI Help Portal
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
